import React, { FC, useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Grid } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { CreateAssetRequestDto, isCrypto } from '@sparling/financial-common'
import { classValidatorResolver } from '@hookform/resolvers/class-validator'
import {
  createAssetRequest,
  getClientTypesForAssignToUserRequest,
  getInstrumentsForCreateAssetRequest,
  getFireblocksAssetBySymbolRequest,
  clearFireblocksByTarget,
  clearFireblocksAll,
} from '../../../../../store/actions'
import { Modal } from '../../../../../components/Modal'
import {
  clientTypesForAssignToUserLoading,
  getClientTypesForAssignToUserEnum,
  getInstrumentsForCreateAssetEnum,
  getInstrumentsForCreateAssetLoading,
  getFireblocksBaseOptions,
  getFireblocksQuoteOptions,
  getFireblocksBaseLoading,
  getFireblocksQuoteLoading,
} from '../../../../../store/selectors'
import { MultiSelect } from '../../../../../components/MultiSelect'
import { SelectOptionFilterType, SelectWithSearch } from '../../../../../components/SelectWithSearch'
import { parseInstrumentLabel } from '../../../../../utils/formatInstrument'
import { FireblocksSelect } from '../../../../../components/FireblocksSelect'

interface PairsCreateModalProps {
  isOpen: boolean
  modalClose: () => void
  existingInstruments: string[]
}

export const filterInstrumentValue: SelectOptionFilterType = (options, state) => {
  const input = state.inputValue.toLowerCase()
  return options.filter((option) => {
    const value = option.value.toString().toLowerCase()
    const label = option.label.toLowerCase()
    const baseValue = value.split('.')[0]
    return label.includes(input) || value.includes(input) || baseValue.includes(input)
  })
}

export const PairsCreateModal: FC<PairsCreateModalProps> = ({ isOpen, modalClose, existingInstruments }) => {
  const dispatch = useDispatch()

  const loadingInstruments = useSelector(getInstrumentsForCreateAssetLoading)
  const instruments = useSelector(getInstrumentsForCreateAssetEnum)

  const filteredInstruments = useMemo(
    () => instruments.filter((i) => !existingInstruments.includes(i.label as string)),
    [instruments, existingInstruments],
  )

  const loadingClientTypesForAssignToUser = useSelector(clientTypesForAssignToUserLoading)
  const clientTypesForAssignToUser = useSelector(getClientTypesForAssignToUserEnum)

  const getClientTypes = useCallback(() => {
    dispatch(getClientTypesForAssignToUserRequest({ search: '' }))
  }, [dispatch])

  const formMethods = useForm<CreateAssetRequestDto>({
    mode: 'all',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    resolver: classValidatorResolver(CreateAssetRequestDto),
  })

  const {
    handleSubmit,
    reset,
    formState: { isValid },
    control,
    watch,
    setValue,
    setError,
    clearErrors,
  } = formMethods

  const selectedInstrumentId = watch('instrument') ?? null
  const baseSelectedId = (watch('fireblockBaseAssetsId') ?? null) as string | null
  const quoteSelectedId = (watch('fireblockQuoteAssetsId') ?? null) as string | null

  const selectedInstrument = useMemo(
    () => filteredInstruments.find((item) => item.value === selectedInstrumentId) ?? null,
    [filteredInstruments, selectedInstrumentId],
  )

  const [baseSymbol, quoteSymbol] = useMemo((): [string | null, string | null] => {
    const label = selectedInstrument?.label?.toString().trim().toUpperCase() || ''
    if (!label) return [null, null]
    if (label.includes('/')) {
      const parts = label.split('/')
      if (parts.length === 2) {
        return [parts[0].trim(), parts[1].trim()]
      }
      return [null, null]
    }
    if (label.length === 6) {
      return [label.slice(0, 3), label.slice(3)]
    }
    return [null, null]
  }, [selectedInstrument])

  const baseIsCrypto = useMemo(() => (baseSymbol ? isCrypto(baseSymbol) : false), [baseSymbol])
  const quoteIsCrypto = useMemo(() => (quoteSymbol ? isCrypto(quoteSymbol) : false), [quoteSymbol])

  const onSubmit = useCallback(
    (data) => {
      if (baseIsCrypto && quoteIsCrypto) {
        let hasError = false
        if (!baseSelectedId) {
          // eslint-disable-next-line
          setError('fireblockBaseAssetsId' as any, { type: 'required', message: 'Base asset is required' })
          hasError = true
        } else {
          // eslint-disable-next-line
          clearErrors('fireblockBaseAssetsId' as any)
        }
        if (!quoteSelectedId) {
          // eslint-disable-next-line
          setError('fireblockQuoteAssetsId' as any, { type: 'required', message: 'Quote asset is required' })
          hasError = true
        } else {
          // eslint-disable-next-line
          clearErrors('fireblockQuoteAssetsId' as any)
        }
        if (hasError) return
      }

      dispatch(createAssetRequest(data))
      reset()
      dispatch(clearFireblocksAll())
      modalClose()
    },
    [dispatch, reset, modalClose, setError, clearErrors, baseIsCrypto, quoteIsCrypto, baseSelectedId, quoteSelectedId],
  )

  const baseAssetOptions = useSelector(getFireblocksBaseOptions)
  const quoteAssetOptions = useSelector(getFireblocksQuoteOptions)
  const baseAssetsLoading = useSelector(getFireblocksBaseLoading)
  const quoteAssetsLoading = useSelector(getFireblocksQuoteLoading)

  useEffect(() => {
    if (baseIsCrypto && baseSymbol) {
      dispatch(getFireblocksAssetBySymbolRequest({ symbol: baseSymbol, target: 'base' }))
    } else {
      dispatch(clearFireblocksByTarget({ target: 'base' }))
    }
  }, [dispatch, baseIsCrypto, baseSymbol])

  useEffect(() => {
    if (quoteIsCrypto && quoteSymbol) {
      dispatch(getFireblocksAssetBySymbolRequest({ symbol: quoteSymbol, target: 'quote' }))
    } else {
      dispatch(clearFireblocksByTarget({ target: 'quote' }))
    }
  }, [dispatch, quoteIsCrypto, quoteSymbol])

  const searchInstrument = useCallback(
    (search: string) => {
      if (search) dispatch(getInstrumentsForCreateAssetRequest({ search }))
    },
    [dispatch],
  )

  useEffect(() => {
    dispatch(getInstrumentsForCreateAssetRequest({ search: '' }))
  }, [dispatch])

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'instrument') {
        const selectedInstrumentItem = filteredInstruments.find((item) => item.value === value.instrument)
        if (selectedInstrumentItem) {
          setValue('underlier', parseInstrumentLabel(selectedInstrumentItem.label))
          setValue('fireblockBaseAssetsId', null)
          setValue('fireblockQuoteAssetsId', null)
          dispatch(clearFireblocksAll())
        }
      }
    })

    return () => subscription.unsubscribe()
  }, [dispatch, watch, filteredInstruments, setValue])

  const onCloseModal = useCallback(() => {
    dispatch(clearFireblocksAll())
    reset()
    modalClose()
  }, [dispatch, reset, modalClose])

  return (
    <Modal
      title={'Create Pair'}
      submitTitle="Create"
      isOpen={isOpen}
      modalClose={onCloseModal}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitDisabled={!isValid || (baseIsCrypto && quoteIsCrypto && (!baseSelectedId || !quoteSelectedId))}
      isWidthAuto
    >
      <FormProvider {...formMethods}>
        <Grid container display={'flex'} justifyContent={'space-between'} minHeight={80}>
          <SelectWithSearch
            inputName={'instrument'}
            labelName={'Instrument'}
            placeholder={'Instrument'}
            control={control}
            selectedId={selectedInstrumentId}
            loading={loadingInstruments}
            options={filteredInstruments}
            onSearch={searchInstrument}
            width={'100%'}
            filterOption={filterInstrumentValue}
          />
          {baseIsCrypto && (
            <FireblocksSelect
              inputName={'fireblockBaseAssetsId'}
              labelName={'Fireblocks Base Asset'}
              placeholder={'Select Fireblocks Base Asset'}
              control={control}
              selectedId={baseSelectedId}
              loading={baseAssetsLoading}
              options={baseAssetOptions}
              required={baseIsCrypto}
              width={'100%'}
            />
          )}
          {quoteIsCrypto && (
            <FireblocksSelect
              inputName={'fireblockQuoteAssetsId'}
              labelName={'Fireblocks Quote Asset'}
              placeholder={'Select Fireblocks Quote Asset'}
              control={control}
              selectedId={quoteSelectedId}
              loading={quoteAssetsLoading}
              options={quoteAssetOptions}
              required={quoteIsCrypto}
              width={'100%'}
            />
          )}
          <MultiSelect
            inputName={'clientType'}
            labelName={'Client Type'}
            placeholder={'Select client type'}
            control={control}
            loading={loadingClientTypesForAssignToUser}
            options={clientTypesForAssignToUser}
            firstLoad={getClientTypes}
          />
        </Grid>
      </FormProvider>
    </Modal>
  )
}
